import { API } from "../../../services";
import { handleResponse } from "../../../helpers";
import { API_URL } from "../../../config/appConfig";
import { normalizeUserList } from "../utils/normalizeUser";

const basePath = `${API_URL.idp}/user`;

const tenantHeaders = (tenantId, extra = {}) => ({
  headers: {
    ...(tenantId ? { tenantId: String(tenantId).trim() } : {}),
    ...extra,
  },
});

const getAllTenantUsers = async ({
  size = 20,
  index = 0,
  enabled,
  tenantId,
  ...rest
} = {}) => {
  const params = {
    size,
    index,
    ...rest,
  };

  if (typeof enabled === "boolean") {
    params.enabled = enabled;
  }

  return API.get(
    basePath,
    "tenant_users_list",
    params,
    {},
    tenantHeaders(tenantId, { skipJsonContentType: true })
  ).then(handleResponse);
};

const getTenantUserById = async (userId, tenantId) => {
  if (!userId) {
    throw new Error("User id is missing.");
  }

  return API.get(
    `${basePath}/${userId}`,
    "tenant_user_by_id",
    {},
    {},
    tenantHeaders(tenantId, { skipJsonContentType: true })
  ).then(handleResponse);
};

const searchTenantUsers = async (filters = {}) => {
  return getAllTenantUsers({
    size: filters.size ?? 20,
    index: filters.index ?? 0,
    username: filters.username || undefined,
    email: filters.email || undefined,
    mobile: filters.mobile || undefined,
    tenantId: filters.tenantId,
  });
};

const buildDisplayName = (payload = {}) => {
  const firstname = String(payload.firstname || "").trim();
  const lastname = String(payload.lastname || "").trim();
  const combined = [firstname, lastname].filter(Boolean).join(" ").trim();
  return combined || String(payload.name || "").trim();
};

const createTenantUser = async (payload = {}, tenantId) => {
  // Tenant APIs accept `name` only (firstname/lastname are rejected).
  const name = buildDisplayName(payload);
  const body = {
    name,
    username: String(payload.username || "").trim(),
    password: payload.password,
    email: String(payload.email || "").trim(),
    mobile: String(payload.mobile || "").trim(),
    roles: Array.isArray(payload.roles)
      ? payload.roles.filter(Boolean).map(String)
      : [],
    clientId: String(payload.clientId || "").trim(),
  };

  return API.post(
    basePath,
    "tenant_user_create",
    body,
    tenantHeaders(tenantId)
  ).then(handleResponse);
};

const updateTenantUser = async (userId, payload = {}, tenantId) => {
  if (!userId) {
    throw new Error("User id is missing.");
  }

  const body = {};
  const hasFirstname = payload.firstname !== undefined;
  const hasLastname = payload.lastname !== undefined;
  const hasName = payload.name !== undefined;

  if (hasFirstname || hasLastname || hasName) {
    const name = buildDisplayName(payload);
    if (name) body.name = name;
  }

  if (payload.email !== undefined) body.email = String(payload.email || "").trim();
  if (payload.mobile !== undefined) {
    body.mobile = String(payload.mobile || "").trim();
  }
  if (payload.mfaEnabled !== undefined) {
    body.mfaEnabled = Boolean(payload.mfaEnabled);
  }

  return API.put(
    `${basePath}/${userId}`,
    "tenant_user_update",
    body,
    tenantHeaders(tenantId)
  ).then(handleResponse);
};

const deleteTenantUser = async (userId, tenantId) => {
  if (!userId) {
    throw new Error("User id is missing.");
  }

  return API.delete(
    `${basePath}/${userId}`,
    "tenant_user_delete",
    { mobile: "" },
    tenantHeaders(tenantId)
  ).then(handleResponse);
};

const updateTenantUserStatus = async (userId, status, tenantId) => {
  if (!userId) {
    throw new Error("User id is missing.");
  }

  return API.put(
    `${basePath}/${userId}/status`,
    "tenant_user_status",
    {
      status: String(status || "").trim(),
    },
    tenantHeaders(tenantId)
  ).then(handleResponse);
};

const changeTenantUserPassword = async (userId, newPassword, tenantId) => {
  if (!userId) {
    throw new Error("User id is missing.");
  }

  return API.put(
    `${basePath}/${userId}/password`,
    "tenant_user_change_password",
    { password: newPassword },
    tenantHeaders(tenantId)
  ).then(handleResponse);
};

const updateTenantUserRole = async (userId, roles, tenantId) => {
  if (!userId) {
    throw new Error("User id is missing.");
  }

  return API.put(
    `${basePath}/${userId}/role`,
    "tenant_user_update_role",
    { roles },
    tenantHeaders(tenantId)
  ).then(handleResponse);
};

const roleAssignmentKeys = (role) => {
  if (role == null) return [];
  if (typeof role === "string") {
    const value = role.trim().toLowerCase();
    return value ? [value] : [];
  }
  return [role.name, role.roleId, role.id, role._id]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter((value, index, list) => value && list.indexOf(value) === index);
};

/**
 * Users store roles by name. After a role is deleted, that name can remain
 * on the user and re-bind if a new role reuses it. Strip it by User Id.
 */
const detachRoleFromTenantUsers = async (role, tenantId) => {
  const keys = new Set(roleAssignmentKeys(role));
  if (!keys.size) return;

  const pageSize = 50;
  const maxPages = 100;
  for (let page = 0; page < maxPages; page += 1) {
    const payload = await getAllTenantUsers({
      size: pageSize,
      index: page,
      tenantId,
    });
    const batch = normalizeUserList(payload);
    if (!batch.length) break;

    const updates = batch
      .map((user) => {
        const roles = Array.isArray(user.roles)
          ? user.roles.map(String).filter(Boolean)
          : [];
        const next = roles.filter(
          (item) => !keys.has(String(item).trim().toLowerCase())
        );
        if (next.length === roles.length) return null;
        const userId = user.userId || user.id;
        if (!userId) return null;
        return updateTenantUserRole(userId, next, tenantId);
      })
      .filter(Boolean);

    if (updates.length) {
      await Promise.all(updates);
    }
    if (batch.length < pageSize) break;
  }
};

export const tenantUserService = {
  getAllTenantUsers,
  getTenantUserById,
  searchTenantUsers,
  createTenantUser,
  updateTenantUser,
  deleteTenantUser,
  updateTenantUserStatus,
  changeTenantUserPassword,
  updateTenantUserRole,
  detachRoleFromTenantUsers,
};
